/* A small SVG line chart for the lab HUDs: axes with a few ticks, series, shaded bands, a moving
   cursor and horizontal marks. It redraws from data every frame (a few hundred points at most), so
   it follows the same rule as the stage: what is on screen is a function of the data and t. */

const NS = "http://www.w3.org/2000/svg";

export interface Series {
  points: [number, number][];
  colour: string;
  width?: number;
  dash?: string;
  dots?: boolean;          // draw a dot at every point
  opacity?: number;
}
export interface Band { lo: [number, number][]; hi: [number, number][]; colour: string; opacity?: number }
export interface Mark { y: number; colour: string; label?: string; dash?: string }

export interface ChartSpec {
  x: [number, number];
  y: [number, number];
  xTicks?: number[];
  yTicks?: number[];
  xFormat?: (v: number) => string;
  yFormat?: (v: number) => string;
  xLabel?: string;
  yLabel?: string;
}

export class Chart {
  readonly svg: SVGSVGElement;
  private readonly w: number;
  private readonly h: number;
  private readonly pad: { l: number; r: number; t: number; b: number };

  constructor(host: HTMLElement, private spec: ChartSpec, w = 520, h = 220, pad = { l: 58, r: 14, t: 12, b: 34 }) {
    this.pad = pad;
    this.w = w;
    this.h = h;
    this.svg = document.createElementNS(NS, "svg");
    this.svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
    this.svg.setAttribute("class", "chart");
    host.appendChild(this.svg);
    if (!document.getElementById("chart-css")) {
      const style = document.createElement("style");
      style.id = "chart-css";
      style.textContent = CHART_CSS;
      document.head.appendChild(style);
    }
  }

  setRange(x?: [number, number], y?: [number, number]): void {
    if (x) this.spec.x = x;
    if (y) this.spec.y = y;
  }

  px(x: number): number {
    const [a, b] = this.spec.x;
    return this.pad.l + (x - a) / (b - a || 1) * (this.w - this.pad.l - this.pad.r);
  }

  py(y: number): number {
    const [a, b] = this.spec.y;
    const v = Math.min(b, Math.max(a, y));
    return this.h - this.pad.b - (v - a) / (b - a || 1) * (this.h - this.pad.t - this.pad.b);
  }

  private path(points: [number, number][]): string {
    return points.map(([x, y], i) => `${i ? "L" : "M"}${this.px(x).toFixed(1)} ${this.py(y).toFixed(1)}`).join("");
  }

  draw({ series = [], bands = [], marks = [], cursor }: { series?: Series[]; bands?: Band[]; marks?: Mark[]; cursor?: number } = {}): void {
    const s = this.spec, fx = s.xFormat ?? String, fy = s.yFormat ?? String;
    const parts: string[] = [];
    const x0 = this.pad.l, x1 = this.w - this.pad.r, y0 = this.pad.t, y1 = this.h - this.pad.b;
    parts.push(`<rect x="${x0}" y="${y0}" width="${x1 - x0}" height="${y1 - y0}" fill="rgba(5,7,13,.45)" stroke="rgba(148,163,184,.18)"/>`);
    for (const t of s.yTicks ?? []) {
      const y = this.py(t);
      parts.push(`<line x1="${x0}" x2="${x1}" y1="${y}" y2="${y}" stroke="rgba(148,163,184,.12)"/>`,
        `<text x="${x0 - 8}" y="${y + 4}" text-anchor="end" class="tick">${fy(t)}</text>`);
    }
    const xs = s.xTicks ?? [];
    xs.forEach((t, i) => {
      const x = this.px(t);
      // the last tick label ends at its tick, so a long one ("200 iterations") stays inside the chart
      const anchor = i === xs.length - 1 && xs.length > 1 ? "end" : "middle";
      parts.push(`<line x1="${x}" x2="${x}" y1="${y1}" y2="${y1 + 5}" stroke="rgba(148,163,184,.4)"/>`,
        `<text x="${x}" y="${y1 + 20}" text-anchor="${anchor}" class="tick">${fx(t)}</text>`);
    });
    if (s.xLabel) parts.push(`<text x="${x1}" y="${this.h - 2}" text-anchor="end" class="axis">${s.xLabel}</text>`);
    if (s.yLabel) parts.push(`<text x="${x0 + 6}" y="${y0 + 14}" class="axis">${s.yLabel}</text>`);
    for (const b of bands) {
      if (!b.lo.length) continue;
      const d = this.path(b.hi) + b.lo.slice().reverse().map(([x, y]) => `L${this.px(x).toFixed(1)} ${this.py(y).toFixed(1)}`).join("") + "Z";
      parts.push(`<path d="${d}" fill="${b.colour}" opacity="${b.opacity ?? .16}"/>`);
    }
    for (const m of marks) {
      const y = this.py(m.y);
      parts.push(`<line x1="${x0}" x2="${x1}" y1="${y}" y2="${y}" stroke="${m.colour}" stroke-dasharray="${m.dash ?? "5 4"}" opacity=".8"/>`);
      if (m.label) parts.push(`<text x="${x1 - 4}" y="${y - 5}" text-anchor="end" class="mark" fill="${m.colour}">${m.label}</text>`);
    }
    parts.push(`<clipPath id="c${this.uid}"><rect x="${x0}" y="${y0 - 2}" width="${x1 - x0}" height="${y1 - y0 + 4}"/></clipPath><g clip-path="url(#c${this.uid})">`);
    for (const sr of series) {
      if (!sr.points.length) continue;
      parts.push(`<path d="${this.path(sr.points)}" fill="none" stroke="${sr.colour}" stroke-width="${sr.width ?? 2.2}" ` +
        `stroke-linejoin="round" stroke-linecap="round" ${sr.dash ? `stroke-dasharray="${sr.dash}"` : ""} opacity="${sr.opacity ?? 1}"/>`);
      if (sr.dots) for (const [x, y] of sr.points) parts.push(`<circle cx="${this.px(x)}" cy="${this.py(y)}" r="3.6" fill="${sr.colour}"/>`);
    }
    parts.push("</g>");
    if (cursor !== undefined) {
      const x = this.px(cursor);
      parts.push(`<line x1="${x}" x2="${x}" y1="${y0}" y2="${y1}" stroke="#e6edf3" stroke-width="1.4" opacity=".75"/>`);
    }
    this.svg.innerHTML = parts.join("");
  }

  private readonly uid = ++charts;   // names this chart's clip path
}

let charts = 0;

/* The chart's own text; added to the page the first time a chart is made. */
const CHART_CSS = `
  .chart { width: 100%; height: auto; display: block; font-family: var(--mono); }
  .chart .tick { font-size: 13px; fill: #6b7a93; }
  .chart .axis { font-size: 13px; fill: #8b98ad; }
  .chart .mark { font-size: 13px; }
`;
